import { Main as Container } from 'Components/Main';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getUsersGroupById, getUsersGroupUsers } from 'services/users-groups-service';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'Components/ui';
import styled from 'styled-components';

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_GRADIENTS = [
  'var(--vo-avatar-grad-1)',
  'var(--vo-avatar-grad-2)',
  'var(--vo-avatar-grad-3)',
  'var(--vo-avatar-grad-4)',
  'var(--vo-avatar-grad-5)',
  'var(--vo-avatar-grad-6)',
  'var(--vo-avatar-grad-7)',
  'var(--vo-avatar-grad-8)',
];

const getGradient = name =>
  AVATAR_GRADIENTS[(name || ' ').charCodeAt(0) % AVATAR_GRADIENTS.length];

const getInitials = name => {
  const parts = (name || '?').trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : (name || '?').substring(0, 2).toUpperCase();
};

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;


const EditBtn = styled.button`
  height: 40px;
  padding: 0 16px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-celeste);
  background: white;
  border: 1.5px solid var(--vo-celeste);
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  gap: 6px;

  &:hover { background: var(--vo-mist); }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Shared card base
const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

// ─── Left card ────────────────────────────────────────────────────────────────

const DetailsCard = styled(Card)`
  padding: 24px;
`;

const CardTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const FieldValue = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-ink);
`;

const MembersCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: var(--vo-celeste);
`;

const StatusBadge = styled.span`
  display: inline-block;
  align-self: flex-start;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  color: ${({ $active }) => ($active ? 'var(--vo-success)' : 'var(--vo-slate)')};
  background: ${({ $active }) => ($active ? 'var(--vo-success-10)' : 'var(--vo-slate-10)')};
`;

// ─── Right card ───────────────────────────────────────────────────────────────

const UsersCard = styled(Card)``;

const UsersCardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const UsersCardTitle = styled.h2`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const UsersCardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: var(--vo-mist);
`;

const Th = styled.th`
  padding: 11px 16px;
  text-align: left;
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 1px solid var(--vo-cloud);
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  &:last-child { border-bottom: none; }
  &:hover { background: var(--vo-capri-25); }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarInitials = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const UserNameText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--vo-ink);
`;

const PermissionText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-ink-2);
`;

const EmptyRow = styled.td`
  padding: 32px 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-steel);
`;

const LoadingText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  padding: 24px;
  margin: 0;
`;

const ErrorWrap = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-danger);
  padding: 32px 30px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const UsersGroupsShow = () => {
  const { t } = useTranslation(['user']);
  const navigate = useNavigate();
  const { id } = useParams();

  const { isLoading, error, data: userGroup } = useQuery(
    ['getUserGroupById', id],
    async () => {
      if (!id) return null;
      const res = await getUsersGroupById(id);
      return res.status === 200 ? res.data : null;
    }
  );

  const { isLoading: isLoadingUsers, data: groupUsers } = useQuery(
    ['getUsersGroupUsers', id],
    async () => {
      if (!id) return [];
      const res = await getUsersGroupUsers(id);
      return res.status === 200 ? res.data : [];
    }
  );

  if (isLoading) return null;
  if (error) return <ErrorWrap>{t('user:groups.show.error')}</ErrorWrap>;

  return (
    <Container>
      <PageWrap>
        <PageHeader
          title={userGroup?.name}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('user:groups.title'), route: '/users-groups' },
            { label: userGroup?.name },
          ]}
          actions={
            <EditBtn onClick={() => navigate(`/users-groups/edit/${id}`)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {t('user:groups.show.editBtn')}
            </EditBtn>
          }
        />

        <ContentGrid>
          {/* Left — Details card */}
          <DetailsCard>
            <CardTitle>{t('user:groups.show.detailsTitle')}</CardTitle>
            <FieldList>
              <FieldItem>
                <FieldLabel>{t('user:groups.show.fieldName')}</FieldLabel>
                <FieldValue>{userGroup?.name}</FieldValue>
              </FieldItem>

              <FieldItem>
                <FieldLabel>{t('user:groups.show.fieldStatus')}</FieldLabel>
                <StatusBadge $active={userGroup?.active}>
                  {userGroup?.active
                    ? t('user:groups.show.table.active')
                    : t('user:groups.show.table.inactive')}
                </StatusBadge>
              </FieldItem>

              <FieldItem>
                <FieldLabel>{t('user:groups.show.fieldMembers')}</FieldLabel>
                <MembersCount>{groupUsers?.length ?? 0}</MembersCount>
              </FieldItem>
            </FieldList>
          </DetailsCard>

          {/* Right — Users card */}
          <UsersCard>
            <UsersCardHeader>
              <UsersCardTitle>{t('user:groups.show.usersTitle')}</UsersCardTitle>
              <UsersCardSubtitle>
                {t('user:groups.show.usersSubtitle', { name: userGroup?.name })}
              </UsersCardSubtitle>
            </UsersCardHeader>

            {isLoadingUsers ? (
              <LoadingText>{t('user:groups.show.loadingUsers')}</LoadingText>
            ) : (
              <Table>
                <Thead>
                  <tr>
                    <Th>{t('user:groups.show.table.name')}</Th>
                    <Th>{t('user:groups.show.table.email')}</Th>
                    <Th>{t('user:groups.show.table.permission')}</Th>
                    <Th>{t('user:groups.show.table.status')}</Th>
                  </tr>
                </Thead>
                <tbody>
                  {groupUsers && groupUsers.length > 0 ? (
                    groupUsers.map(user => (
                      <Tr key={user._id}>
                        <Td>
                          <NameCell>
                            <Avatar $gradient={getGradient(user.name)}>
                              <AvatarInitials>{getInitials(user.name)}</AvatarInitials>
                            </Avatar>
                            <UserNameText>{user.name}</UserNameText>
                          </NameCell>
                        </Td>
                        <Td>{user.email}</Td>
                        <Td>
                          <PermissionText>
                            {user.type || user.role || user.permission || '—'}
                          </PermissionText>
                        </Td>
                        <Td>
                          <StatusBadge $active={user.active}>
                            {user.active
                              ? t('user:groups.show.table.active')
                              : t('user:groups.show.table.inactive')}
                          </StatusBadge>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <tr>
                      <EmptyRow colSpan={4}>{t('user:groups.show.noUsers')}</EmptyRow>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </UsersCard>
        </ContentGrid>
      </PageWrap>
    </Container>
  );
};
